import React, { useState, useEffect, useRef } from "react";
import MaterialTable from "@material-table/core";
import axios, { jobClient } from "../AxiosClient";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import moment from "moment";
import JobProgress from "./components/JobProgress";
import { useInterval } from "../utils/KpsUtils.js";

function JobManager(props) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState([]);
  const [jobToStop, setJobToStop] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const tableRef = useRef(null);

  const [columns, setColumns] = useState([]);

  useEffect(
    () => {
      setColumns([
        {
          title: t("job_manager_job_name"),
          field: "jobName",
        },
        {
          title: t("job_manager_job_status"),
          field: "jobStatus",
          lookup: {
            COMPLETED: "Complete",
            CANCELLED: "Cancelled",
            ERRORED: "Errored",
            RUNNING: "Running",
            QUEUED: "Queued",
          },
          defaultFilter: ["RUNNING", "QUEUED"],
        },
        {
          title: t("job_manager_job_error"),
          field: "error",
        },
        {
          title: t("job_manager_job_progress"),
          field: "jobName",
          sorting: false,
          filtering: false,
          render: (rowData) => {
            if (rowData.jobStatus !== "RUNNING" || !rowData.statusDTO) {
              return null;
            }
            return <JobProgress status={rowData.statusDTO} />;
          },
        },
        {
          title: t("job_manager_job_started"),
          field: "jobStarted",
          defaultSort: "desc",
          type: "datetime",
          render: (rowData) => {
            if (!rowData.jobStarted) return null;
            return moment(rowData.jobStarted).format("DD/MM/YYYY HH:mm:ss");
          },
        },
        {
          title: t("job_manager_job_finished"),
          field: "jobFinished",
          type: "datetime",
          render: (rowData) => {
            if (!rowData.jobFinished) return null;
            return moment(rowData.jobFinished).format("DD/MM/YYYY HH:mm:ss");
          },
        },
        {
          title: t("job_manager_next_fire_time"),
          field: "nextFireTime",
          sorting: false,
          filtering: false,
          type: "datetime",
          emptyValue: "N/A",
          render: (rowData) => {
            if (!rowData.nextFireTime) return null;
            return moment(rowData.nextFireTime).format("DD/MM/YYYY HH:mm:ss");
          },
        },
      ]);
    },
    [t]
  );

  const doStop = () => {
    setStopDialogOpen(false);
    jobClient.post(`/instance/${jobToStop.jobFireInstanceId}/stop`).then(() => {
      enqueueSnackbar(t("job_manager_job_stopped"), {
        variant: "success",
      });
      setJobToStop({});
    });
  };

  const doCancel = () => {
    setCancelDialogOpen(false);
    jobClient.post(`/job/${jobToCancel.triggerName}/cancel`).then(() => {
      enqueueSnackbar(t("job_manager_job_cancelled"), {
        variant: "success",
      });
      setJobToCancel({});
    });
  };

  useInterval(() => {
    // poll for jobs
    tableRef.current && tableRef.current.onQueryChange();
  }, 10000);

  const getData = (query, resolve, reject) => {
    jobClient
      .post("/job/filter", {
        page: query.page,
        size: query.pageSize,
        sort: query.orderBy ? query.orderBy.field : "jobStarted",
        sortDirection: query.orderDirection,
        ...processFilters(query.filters),
      })
      .then((res) => {
        resolve({
          data: res.data.content,
          page: res.data.number,
          totalCount: res.data.totalElements,
        });
      })
      .catch((err) => {
        reject(err);
      });
  };

  const processFilters = (tableFilters) => {
    let filters = {};

    tableFilters.forEach((filter) => {
      switch (filter.column.field) {
        case "jobStatus":
          if (!!filter.value && filter.value.length > 0) {
            filters.jobStatus = filter.value;
          }
          break;
        case "jobName":
          if (!!filter.value) {
            filters.jobName = filter.value;
          }
          break;
        case "error":
          if (!!filter.value) {
            filters.error = filter.value;
          }
          break;
        case "jobStarted":
          if (!!filter.value) {
            filters.jobStartedAfter = filter.value.toISOString();
          }
          break;
        case "jobFinished":
          if (!!filter.value) {
            filters.jobFinishedAfter = filter.value.toISOString();
          }
          break;
        case "nextFireTime":
          if (!!filter.value) {
            filters.nextFireTime = filter.value.toISOString();
          }
          break;
      }
    });

    return filters;
  };

  return (
    <div
      style={{
        overflow: "auto",
        padding: 10,
        height: "100%",
        backgroundColor: "#eae0d7",
        backgroundImage: "url('../images/background.svg')",
      }}
    >
      <MaterialTable
        tableRef={tableRef}
        columns={columns}
        data={(query) =>
          new Promise((resolve, reject) => {
            getData(query, resolve, reject);
          })
        }
        actions={[
          (rowData) => ({
            hidden: rowData.jobStatus !== "RUNNING",
            icon: "stop",
            tooltip: t("global_stop"),
            onClick: (_e, rowData) => {
              setJobToStop(rowData);
              setStopDialogOpen(true);
            },
          }),
          (rowData) => ({
            hidden: rowData.jobStatus !== "QUEUED",
            icon: "cancel",
            tooltip: t("global_cancel"),
            onClick: (_e, rowData) => {
              setJobToCancel(rowData);
              setCancelDialogOpen(true);
            },
          }),
        ]}
        options={{
          pageSizeOptions: [5, 10, 15, 20],
          pageSize: 10,
          filtering: true,
          headerStyle: { position: "sticky", top: 0 },
          actionsColumnIndex: -1,
          loadingType: "linear",
          debounceInterval: 300,
        }}
        title={t("global_job_plural")}
      />

      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t("global_are_you_sure")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("job_manager_cancel_queued_confirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} color="primary">
            {t("global_cancel")}
          </Button>
          <Button onClick={doCancel} color="primary" autoFocus>
            {t("global_ok")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={stopDialogOpen}
        onClose={() => setStopDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t("global_are_you_sure")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("job_manager_stop_running_job_confirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopDialogOpen(false)} color="primary">
            {t("global_cancel")}
          </Button>
          <Button onClick={doStop} color="primary" autoFocus>
            {t("global_ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default JobManager;
